import React, { useEffect, useState } from "react";
import { Throwable } from "@wss/error-tracking/throwable";
import { logException } from "@wss/error-tracking";
import { toError } from "@wss/error-tracking/utils";
import Modal, { Body, Footer, Header } from "~/common/ModalReact";
import { UserAutoReorder } from "~/ProductDetails/ProductDetailPageResult";
import { AddToExistingAutoReorderResponse } from "./existingAutoReorderRequest";
import Button from "~/common/Buttons";
import Notification from "~/common/Notification";
import(/* webpackChunkName: "modals-standalone" */"~/bootstrap/less/modals-standalone.less");
interface Props {
  existingAutoReorder: UserAutoReorder;
  addToExistingAutoReorderResponse: AddToExistingAutoReorderResponse;
  setAddToExistingAutoReorderResponse: React.Dispatch<React.SetStateAction<AddToExistingAutoReorderResponse>>;
}
const CommonCarrierSettingsModal = ({
  existingAutoReorder,
  addToExistingAutoReorderResponse,
  setAddToExistingAutoReorderResponse
}: Props) => {
  const {
    hasResidential,
    customerLiftgateFee
  } = addToExistingAutoReorderResponse;
  const errorStyle = {
    zIndex: 2,
    fontWeight: 600,
    marginTop: "2px",
    color: "#cf3700",
    lineHeight: "1.2em",
    top: "10px",
    fontSize: ".8em",
    textAlign: "center" as const
  };
  const [liftGateSelection, setLiftGateSelection] = useState("");
  const [callBeforeDeliverySelection, setCallBeforeDeliverySelection] = useState("");
  const [displayError, setDisplayError] = useState({
    showError: false,
    message: ""
  });
  useEffect(() => {
    setCallBeforeDeliverySelection(hasResidential ? "1" : "");
  }, [hasResidential]);
  const handleSaveAndCloseModal = () => {
    setAddToExistingAutoReorderResponse({
      hasResidential: false,
      customerLiftgateFee: 0,
      isChangedToCommonCarrier: false
    });
    new Notification({
      heading: "Common carrier settings saved.",
      href: `/myaccount:autoreorder/detail/?r=${existingAutoReorder.id}`,
      body: "",
      buttonText: "View Auto Reorder"
    });
  };
  const handleCloseModal = () => {
    setAddToExistingAutoReorderResponse({
      hasResidential: false,
      customerLiftgateFee: 0,
      isChangedToCommonCarrier: false
    });
    new Notification({
      heading: "Default common carrier settings applied.",
      href: `/myaccount:autoreorder/detail/?r=${existingAutoReorder.id}`,
      body: "",
      buttonText: "View Auto Reorder"
    });
  };
  async function saveCommonCarrierSettings(): Promise<Throwable> {
    const body = new FormData();
    body.append("autoReorderId", existingAutoReorder.id.toString());
    body.append("liftGate", liftGateSelection);
    body.append("callBeforeDelivery", callBeforeDeliverySelection);
    try {
      const response = await fetch("/myaccount:autoreorder/savecommoncarriersettings/", {
        body,
        method: "POST",
        credentials: "include",
        headers: {
          accept: "application/json"
        }
      });
      if (response.ok) {
        handleSaveAndCloseModal();
      } else {
        setDisplayError({
          showError: true,
          message: "Error saving common carrier settings"
        });
      }
      return {
        isError: false
      };
    } catch (e) {
      const error = new Error("Error in saveCommonCarrierSettings", {
        cause: toError(e)
      });
      await logException(error);
      return {
        error,
        isError: true
      };
    }
  }
  const handleSave = () => {
    if (liftGateSelection === "" || callBeforeDeliverySelection === "") {
      setDisplayError({
        showError: true,
        message: "You must pick selections."
      });
    } else {
      setDisplayError({
        showError: false,
        message: ""
      });
      saveCommonCarrierSettings();
    }
  };
  const handleLiftGateSelectionChange = ({
    target: {
      value
    }
  }: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setLiftGateSelection(value);
  };
  const handleCallBeforeDeliverySelectionChange = ({
    target: {
      value
    }
  }: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setCallBeforeDeliverySelection(value);
  };
  const getDescription = () => {
    return <p className="text-base" data-testid="common-carrier-description" data-sentry-component="getDescription" data-sentry-source-file="CommonCarrierSettingsModal.tsx">
        Auto reorder #{existingAutoReorder.id}{" "}
        {existingAutoReorder.name.length > 0 && `(${existingAutoReorder.name}) `}
        will <strong>now ship via common carrier</strong>. Please choose your
        delivery preferences:
      </p>;
  };
  const getLiftGateSection = () => {
    const liftGateFee = customerLiftgateFee > 0 ? customerLiftgateFee : customerLiftgateFee.toFixed(2);
    const liftGateFeeDisplay = <span id="customerLiftgateFee">(${liftGateFee} fee) </span>;
    return <div className="modal--cc__row" data-sentry-component="getLiftGateSection" data-sentry-source-file="CommonCarrierSettingsModal.tsx">
        <div className="modal--cc__label">
          <span className="title" data-testid="lift-gate-title">
            Liftgate {liftGateFeeDisplay}
            <a className="small" href="/policies.html#ShippingPolicyLiftgate" target="_blank">
              <i className="icon-question pr-2" />
            </a>
          </span>
          <span className="small" />
        </div>
        <div className="absolute right-10">
          <select name="lgYesNoSwitch" id="lgYesNoSwitchModal" onChange={handleLiftGateSelectionChange} data-testid="lift-gate-select" defaultValue="">
            <option value="" disabled>
              {" "}
            </option>
            <option value="0">No</option>
            <option value="1">Yes</option>
          </select>
        </div>
      </div>;
  };
  const getCallBeforeDeliverySection = () => {
    return <div className="modal--cc__row" data-sentry-component="getCallBeforeDeliverySection" data-sentry-source-file="CommonCarrierSettingsModal.tsx">
        <div className="modal--cc__label">
          <span className="title" data-testid="callBeforeDeliveryTitle">
            Call Before Delivery{" "}
            <a className="small" href="/policies.html#ShippingPolicyCallBeforeDelivery" target="_blank">
              <i className="icon-question pr-2" />
            </a>
          </span>
        </div>
        <div className="absolute right-10">
          <select name="cbdYesNoSwitch" id="cbdYesNoSwitchModal" disabled={hasResidential} onChange={handleCallBeforeDeliverySelectionChange} data-testid="call-before-delivery-select" defaultValue={hasResidential ? "1" : ""}>
            <option value="" disabled>
              {" "}
            </option>
            <option value="0">No</option>
            <option value="1">Yes</option>
          </select>
        </div>
      </div>;
  };
  return <Modal isOpen={addToExistingAutoReorderResponse.isChangedToCommonCarrier} onRequestClose={() => handleCloseModal()} className="modal modal-plus md:max-w-md block" data-sentry-element="Modal" data-sentry-component="CommonCarrierSettingsModal" data-sentry-source-file="CommonCarrierSettingsModal.tsx">
      <Header closeModal={handleCloseModal} data-sentry-element="Header" data-sentry-source-file="CommonCarrierSettingsModal.tsx">
        <p className="header-2 no-margin" id="myModalLabel" data-testid="commonCarrierSettingsModalHeader">
          <i className="icon-attention warning" style={{
          color: "#e3861f"
        }} />{" "}
          &nbsp;Update Shipping Options
        </p>
      </Header>
      <Body data-sentry-element="Body" data-sentry-source-file="CommonCarrierSettingsModal.tsx">
        {getDescription()}
        <div className="modal--cc__options" data-testid="commonCarrierSettingsModalOptions">
          {getLiftGateSection()}
          {getCallBeforeDeliverySection()}
        </div>
        {displayError.showError && <p style={errorStyle} data-testid="common-carrier-settings-save-error">
            {displayError.message}
          </p>}
      </Body>
      <Footer data-sentry-element="Footer" data-sentry-source-file="CommonCarrierSettingsModal.tsx">
        <Button aria-label="Save" color="green" type="submit" className="btn btn-primary" data-testid="update-shipping-button" onClick={handleSave} data-sentry-element="Button" data-sentry-source-file="CommonCarrierSettingsModal.tsx">
          Update Shipping
        </Button>
      </Footer>
    </Modal>;
};
export default CommonCarrierSettingsModal;